/* eslint-disable @typescript-eslint/no-duplicate-enum-values */

import type { DirectDeposit, DirectDepositUpdate } from '~/types/demo'
import type { PersonalInfo } from '~/types/recruit'
import type { Address } from '~/types/account'
import type {RuleExpression} from "vee-validate";
import type {ArrayOfTOneOrMore} from "~/types/utility";

/**
 * Supported regions
 */
export enum RegionCode {
  NA = 'NA',
  EU = 'EU',
  SP = 'SP'
}

/**
 * Supported ISO countries
 *
 * @export
 * @enum {number}
 */
export enum CountryCode {
  US = 'US',
  CA = 'CA',
  GB = 'GB',
  NL = 'NL',
  DE = 'DE',
  AT = 'AT',
  FR = 'FR',
  AU = 'AU',
  NZ = 'NZ',
  BE = 'BE',
  IE = 'IE'
}

/**
 * Countries that support PayPal Pay Later (Pay in 4).
 * The SDK should include paylater for these countries so marks and messaging work correctly.
 */
export const PAYLATER_SUPPORTED_COUNTRIES: CountryCode[] = [
  CountryCode.AU,
  CountryCode.DE,
  CountryCode.FR,
  CountryCode.GB
]

/**
 * Supported cultures ($language-$country merged)
 *
 * @export
 * @enum {number}
 */
export enum Culture {
  EN_US = 'en-us',
  FR_CA = 'fr-ca',
  EN_CA = 'en-ca',
  EN_GB = 'en-gb',
  NL_NL = 'nl-nl',
  DE_DE = 'de-de',
  DE_AT = 'de-at',
  FR_FR = 'fr-fr',
  EN_AU = 'en-au',
  EN_NZ = 'en-nz',
  NL_BE = 'nl-be',
  FR_BE = 'fr-be',
  DE_BE = 'de-be',
  EN_IE = 'en-ie'
}

/**
 * Supported language path
 *
 * @export
 * @enum {number}
 */
export enum LanguagePath {
  EN_US = '',
  FR_CA = '/fr',
  EN_CA = '',
  EN_GB = '',
  NL_NL = '',
  DE_DE = '',
  DE_AT = '',
  FR_FR = '',
  EN_AU = '',
  EN_NZ = '',
  NL_BE = '',
  FR_BE = '/fr',
  DE_BE = '/de',
  EN_IE = ''
}

/**
 * Supported domains
 *
 * @export
 * @enum {number}
 */
export enum StampinupDomain {
  EN_US = 'stampinup.com',
  EN_CA = 'stampinup.ca',
  FR_CA = 'stampinup.ca',
  EN_GB = 'stampinup.uk',
  NL_NL = 'stampinup.nl',
  DE_DE = 'stampinup.de',
  DE_AT = 'stampinup.at',
  FR_FR = 'stampinup.fr',
  EN_AU = 'stampinup.com.au',
  EN_NZ = 'stampinup.nz',
  NL_BE = 'stampinup.be',
  FR_BE = 'stampinup.be',
  DE_BE = 'stampinup.be',
  EN_IE = 'stampinup.ie'
}

/**
 * Supported env-prefixed domains
 *
 * @export
 * @enum {string}
 */
export enum StampinupDomainEnvPrefix {
  Local= 'local.',
  DEV = 'dev.',
  QA = 'qa.',
  Release = 'www.',
  Non =''
}

/**
 * Supported flags
 *
 * @export
 * @enum {number}
 */
export enum FlagIcon {
  US = 'flag-us',
  CA = 'flag-ca',
  GB = 'flag-gb',
  NL = 'flag-nl',
  DE = 'flag-de',
  AT = 'flag-at',
  FR = 'flag-fr',
  AU = 'flag-au',
  NZ = 'flag-nz',
  BE = 'flag-be',
  IE = 'flag-ie'
}

/**
 * Supported currencies
 *
 * @export
 * @enum {number}
 */
export enum Currency {
  USD = 'USD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  AUD = 'AUD',
  NZD = 'NZD'
}

/**
 * Supported languages
 *
 * @export
 * @enum {string}
 */
export enum Language {
    NL = 'DUT',
    DE = 'GER',
    FR = 'FRE',
    EN = 'ENG',
    NA = 'NA'
}

/**
 * Supported states of a country
 *
 * @export
 * @interface State
 */
export interface State {
  code: string
  name: string
}

/**
 * Data format from /address/countries
 *
 * @export
 * @interface CountryAPIData
 */
export interface CountryAPIData {
  name: string
  code: string
  cultures: string[]
  suRegion: string
  stateType: string
  stateFieldName: string
  states: State[]
}

/**
 * Enum with fields that have both country and region
 *
 * @export
 * @enum RegionAndCountry
 */
export enum RegionAndCountryFields {
  addressConfig = 'addressConfig',
  billingAddressConfig = 'billingAddressConfig',
  phoneNumberFormat = 'phoneNumberFormat',
  phoneNumberRules = 'phoneNumberRules',
  vatRules = 'vatRules',
  demoFinderRules = 'demoFinderRules',
  catalogMailing = 'catalogMailing',
  idaTaxInformation = 'idaTaxInformation',
  idaPersonalInfo = 'idaPersonalInfo',
  idaSupportingTaxMask = 'idaSupportingTaxMask',
  enabledFeatures = 'enabledFeatures'
}

export interface PersonalInfoField {
  name: keyof PersonalInfo
  type: 'text' | 'combobox'| 'birthday' | 'remove' | 'gender' | 'language' | 'state'
  sequence?: number
  rules?: string | Record<string, unknown>
  mask?: string
}

export interface AddressFormField {
  name: string
  type: 'text' | 'state' | 'name' | 'phone' | 'autocomplete' | 'remove'
  line?: number
  'data-recurly'?: string
  sequence?: number
  rules?: RuleExpression<unknown>
  mask?: string
  tokens?: 'phone'
}

export interface EftFormField {
  name: keyof Omit<DirectDepositUpdate, 'isDefaultPayment'>
  key: string
  type: 'text' | 'type'
  rules?: RuleExpression<unknown>
  confirm?: boolean
  mask?: string
  placeholderField?: keyof DirectDeposit
}

export type AddressKey = keyof Address

export type AddressDisplayRow = AddressKey | AddressKey[]

export interface AddressFormConfig {
  fields: AddressFormField[]
  display?: AddressDisplayRow[]
  validate?: boolean
}

/**
 * Fields that have both country and region. Region could be overrided on country level
 *
 * @interface RegionAndCountry
 */
interface RegionAndCountry {
  [RegionAndCountryFields.addressConfig]?: AddressFormConfig
  [RegionAndCountryFields.billingAddressConfig]?: AddressFormConfig
  [RegionAndCountryFields.phoneNumberFormat]?: string
  [RegionAndCountryFields.phoneNumberRules]?: string,
  [RegionAndCountryFields.vatRules]?: string,
  [RegionAndCountryFields.demoFinderRules]?: string
  [RegionAndCountryFields.catalogMailing]?: boolean
  [RegionAndCountryFields.idaTaxInformation]?: boolean
  [RegionAndCountryFields.idaPersonalInfo]?: PersonalInfoField[]
  [RegionAndCountryFields.idaSupportingTaxMask]?: string
  [RegionAndCountryFields.enabledFeatures]?: string[]
}

/**
 * Supported region country data
 *
 * @export
 * @interface Country
 */
export interface Country extends RegionAndCountry {
  name: string
  culture: Culture
  countryCode: CountryCode
  displayCode: string
  icon: FlagIcon
  active: boolean
  currency: Currency
  states?: State[]
  stateFieldName?: string,
  availableLanguages: ArrayOfTOneOrMore<Language>
}

export interface Locale {
  countryCode: CountryCode
  culture: Culture
}

/**
 * Supported regions
 *
 * @export
 * @interface Region
 */
export interface Region extends RegionAndCountry {
  name: string
  regionCode: RegionCode
  countries: ArrayOfTOneOrMore<Country>
}

export interface EftCountry {
  creditAgreement: boolean
  countries: CountryCode[]
  fields: EftFormField[]
  debitReadonlyFieldsDisplay?: {field: string, label: string}[]
}

export interface BillingCountry {
    countryCode: string
    name: string
    defaultCulture?: string
    billingAddressConfig?: AddressFormConfig
}

export interface BillingRegion {
  region: string
  billingAddressConfig: AddressFormConfig
  countries: BillingCountry[]
}

export enum LocaleSource {
  CookieOrUserSettings = 'CookieOrUserSettings',
  HTTPHostHeader = 'HTTPHostHeader',
  DefaultOrFailBack = 'DefaultOrFailBack'
}

export interface SwitchLocalePayload {
  culture: Culture
}

/**
 * Payload to set up locale
 *
 * @export
 * @interface SetLocalePayload
 */
export interface SetLocalePayload {
  culture: Culture
  localeSource: LocaleSource
}

export type OptionalPropertyAccess<T extends string, P> = T extends keyof P ? P[T] : null
export type CountryOrRegionValue<T extends string> = OptionalPropertyAccess<T, Region & Country>
